'use client';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ReactNode } from 'react';
import type { TableProps } from 'react-aria-components';
import { Table as AriaTable } from 'react-aria-components';

import { useFusionTheme } from '../../hooks';
import { DEFAULT_TABLE_VARIANT, propagateVariantToChildren } from '../utils';

import styles from './Table.module.scss';

export type { SortDescriptor } from 'react-aria-components';

export const tableVariant = cva(styles.container, {
  variants: {
    fitContent: {
      true: styles.fitContent
    },
    hasBorder: {
      true: styles.border
    }
  }
});

type SharedProps = VariantProps<typeof tableVariant> & {
  className?: string;
  children?: ReactNode;
};

export type DefaultTableProps = {
  variant?: 'default';
} & Pick<
  TableProps,
  | 'slot'
  | 'aria-label'
  | 'aria-labelledby'
  | 'aria-describedby'
  | 'aria-details'
  | 'sortDescriptor'
  | 'onSortChange'
  | 'selectionMode'
  | 'defaultSelectedKeys'
> &
  SharedProps;

type BasicTableProps = {
  variant: 'basic';
} & SharedProps;

export type Props = DefaultTableProps | BasicTableProps;

const Table = ({
  variant = DEFAULT_TABLE_VARIANT,
  className,
  fitContent,
  hasBorder,
  children,
  ...props
}: Props) => {
  const { addThemeClassName } = useFusionTheme();
  const classes = addThemeClassName(tableVariant({ fitContent, hasBorder }), className);

  if (variant === 'basic') {
    return <table className={classes}>{propagateVariantToChildren(children)}</table>;
  }

  return (
    <AriaTable {...props} className={classes}>
      {children}
    </AriaTable>
  );
};

export default Table;
